import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowRight, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "../components/ui/skeleton";
import { API, formatAUD, downloadInvoice } from "../lib/shop";

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => setError(true));
  }, [orderId]);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <p className="text-lg font-semibold">Order not found.</p>
        <Link to="/" className="mt-2 inline-block text-secondary underline">Back to catalogue</Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="mt-6 h-64 rounded-sm" />
      </div>
    );
  }

  const a = order.address;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 md:px-8" data-testid="order-confirmation-page">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="h-10 w-10 text-[#16a34a]" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Order placed!</h1>
          <p className="text-sm text-muted-foreground">
            Order <span className="font-mono font-semibold text-primary" data-testid="order-number">{order.order_number}</span>
            {order.payment_status === "paid" && (
              <span className="ml-2 rounded-sm bg-[#16a34a] px-1.5 py-0.5 text-xs font-bold text-white" data-testid="order-paid-badge">PAID</span>
            )}
            {" "}— a confirmation email has been sent to {order.customer_email}.
          </p>
        </div>
      </div>

      <div className="mt-8 rounded-sm border border-border bg-white">
        <div className="border-b border-border bg-muted px-6 py-3 text-sm font-semibold">Items</div>
        <ul className="divide-y divide-border">
          {order.items.map((i) => (
            <li key={i.variant_id} className="flex items-center justify-between gap-4 px-6 py-3 text-sm">
              <div>
                <div className="font-medium">{i.product_name}</div>
                <div className="text-xs text-muted-foreground">
                  {i.description}{i.insulation_colour ? ` · ${i.insulation_colour}` : ""} · <span className="font-mono">{i.product_code}</span> × {i.quantity}
                </div>
              </div>
              <span className="tabular-nums font-semibold">{formatAUD(i.subtotal)}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-between border-t border-border px-6 py-4 text-base font-bold">
          <span>Total (AUD)</span>
          <span className="tabular-nums text-primary" data-testid="order-total">{formatAUD(order.total)}</span>
        </div>
      </div>

      <div className="mt-6 rounded-sm border border-border bg-white p-6 text-sm">
        <h2 className="font-semibold">Delivery to</h2>
        <p className="mt-2 text-muted-foreground" data-testid="order-address">
          {order.customer_name}<br />
          {a.line1}{a.line2 ? <>, {a.line2}</> : null}<br />
          {a.suburb} {a.state} {a.postcode}, {a.country}
        </p>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Link
          to="/"
          data-testid="continue-shopping-link"
          className="inline-flex items-center gap-2 rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-secondary"
        >
          Continue shopping <ArrowRight className="h-4 w-4" />
        </Link>
        {order.payment_status === "paid" && (
          <button
            onClick={() => downloadInvoice(axios, order).catch(() => toast.error("Could not download invoice"))}
            data-testid="download-invoice-button"
            className="inline-flex items-center gap-2 rounded-sm border border-primary px-5 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            <FileDown className="h-4 w-4" /> Download Tax Invoice (PDF)
          </button>
        )}
      </div>
    </div>
  );
}
